import { useState } from 'react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Modal } from '@/components/ui/Modal';
import { classesApi } from '@/features/institute/classes/api/classes.api';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { capitalizeWords } from '@/lib/format';
import type { AllocatableClass } from '@/types/allocation';

export interface SectionSubjectsModalProps {
  instituteClass: AllocatableClass;
  section: AllocatableClass['sections'][number];
  onClose: () => void;
  /** Raised once saved, so the grid re-reads what the section teaches. */
  onSaved: () => void;
}

/**
 * Which of its class's subjects one section teaches — 11-A Science, 11-B
 * Commerce under one Class 11.
 *
 * Every subject starts ticked, which is what a section teaches until the office
 * says otherwise; ticking them all again puts it back to that default. The
 * server refuses to drop a subject somebody is still allotted here, and says so
 * by name.
 */
export function SectionSubjectsModal({
  instituteClass,
  section,
  onClose,
  onSaved,
}: SectionSubjectsModalProps) {
  const [chosen, setChosen] = useState<Set<string>>(
    () => new Set(section.subjectUuids ?? instituteClass.subjects.map((subject) => subject.uuid)),
  );
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  function toggle(uuid: string) {
    setChosen((current) => {
      const next = new Set(current);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  }

  async function save() {
    setError(null);
    setIsSaving(true);
    try {
      const everything = chosen.size === instituteClass.subjects.length;
      await classesApi.setSectionSubjects(section.uuid, everything ? null : [...chosen]);
      toast.success(
        everything
          ? `${section.displayName} teaches every subject of ${instituteClass.name}`
          : `${section.displayName} teaches ${chosen.size} of ${instituteClass.subjects.length} subjects`,
        'Allotment, homework and the student panel follow straight away.',
      );
      onSaved();
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="sm"
      title={`Subjects in ${section.displayName}`}
      description={`Untick what this section does not take. Every subject comes from ${instituteClass.name}'s list.`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={() => void save()} isLoading={isSaving} disabled={chosen.size === 0}>
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error && (
          <Alert tone="critical" title="Could not save">
            {error}
          </Alert>
        )}
        {chosen.size === 0 && (
          <p className="text-xs text-ink-3">A section has to teach at least one subject.</p>
        )}
        <ul className="flex flex-col gap-2">
          {instituteClass.subjects.map((subject) => (
            <li key={subject.uuid}>
              <Checkbox
                label={capitalizeWords(subject.name)}
                checked={chosen.has(subject.uuid)}
                onChange={() => toggle(subject.uuid)}
              />
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}
